import { List as ImList, Map as ImMap, Record, type RecordOf } from 'immutable';
import { DateTime } from 'luxon';
import type { QcsSyncResourceStoreProps } from '../core/resourceStoreTypes';

export type FiIssueType = 'INFO' | 'SCHEDULED_OUTAGE' | 'PARTIAL_OUTAGE' | 'FULL_OUTAGE';
export type FiChannel =
  | 'FDP_DEFAULT'
  | 'FDP_OFX'
  | 'FDP_WSI_OAUTH'
  | 'FINICITY'
  | 'PLAID'
  | 'PLAID_SANDBOX'
  | 'PLAID_DEV'
  | 'STRIPE'
  | 'STRIPE_TEST';

export enum AllowedAggregators {
  FDS = 'fds',
  FDP = 'fdp',
  FINICITY = 'finicity',
  PLAID = 'plaid',
}

export type InstitutionFormField = {
  key: string;
  name: string;
  type: string;
  displayOrder: number;
  displayLabel: string;
  helpText: string | null;
  maxLength: number | null;
  encrypt?: boolean;
};

export const mkInstitutionFormField = Record<InstitutionFormField>({
  key: 'unknown',
  name: 'unknown',
  type: 'unknown',
  displayOrder: 0,
  displayLabel: 'unknown',
  helpText: null,
  maxLength: null,
  encrypt: undefined,
});

export type InstitutionPartnerAuth = {
  intuitProperty: string;
  offeringRedirectUri: string;
  partnerAuthUrl: string;
  partnerUid: string;
  fields: ImList<RecordOf<InstitutionFormField>>;
};

export const mkInstitutionPartnerAuth = Record<InstitutionPartnerAuth>({
  intuitProperty: 'unknown',
  offeringRedirectUri: 'unknown',
  partnerAuthUrl: 'unknown',
  partnerUid: 'unknown',
  fields: ImList(),
});

export type InstitutionFormEncryption = {
  keyId?: string;
  expiresAt?: string;
  algorithm?: string;
  publicKey?: string;
  signature?: string;
  signatureAlgorithm?: string;
};

export const mkInstitutionFormEncryption = Record({
  keyId: undefined,
  expiresAt: undefined,
  algorithm: undefined,
  publicKey: undefined,
  signature: undefined,
  signatureAlgorithm: undefined,
});

export type InstitutionChannelData = {
  type?: string; // TODO - narrow these types.
  authType?: string;
  formFields?: ImList<RecordOf<InstitutionFormField>> | null;
  formEncryption?: RecordOf<InstitutionFormEncryption>;
  formSignature?: string;
  formSignatureAlgorithm?: string;
  partnerAuth?: RecordOf<InstitutionPartnerAuth> | null;
};

export const mkInstitutionChannelData = Record<InstitutionChannelData>({
  type: undefined,
  authType: undefined,
  formFields: undefined,
  formEncryption: undefined,
  formSignature: undefined,
  formSignatureAlgorithm: undefined,
  partnerAuth: undefined,
});

export type InstitutionOfxData = {
  bid: 0;
  pid: 0;
  name: string;
  contact_url: string;
  contact_phone: string;
  registraction_url: string;
};

export const mkInstitutionOfxData = Record<InstitutionOfxData>({
  bid: 0,
  pid: 0,
  name: 'unknown',
  contact_url: 'unknown',
  contact_phone: 'unknown',
  registraction_url: 'unknown',
});

export type InstitutionFdsData = {
  id: string;
  name: string;

  contact_url: string;
  contact_phone: string;
  contact_email: string;

  login_url: string;

  channelData?: RecordOf<InstitutionChannelData>;
  ewcLive?: boolean;
};

export const mkInstitutionFdsData = Record<InstitutionFdsData>({
  id: '0',
  name: 'unknown',

  contact_url: 'unknown',
  contact_phone: 'unknown',
  contact_email: 'unknown',

  login_url: 'unknown',

  channelData: undefined,
  ewcLive: undefined,
});

export type InstitutionFdpData = {
  id?: string;
  name?: string;

  contact_url?: string;
  contact_phone?: string;
  contact_email?: string;

  login_url?: string;

  channelData?: RecordOf<InstitutionChannelData>;
  channelDataByType?: ImMap<string, RecordOf<InstitutionChannelData>>;
};

export const mkInstitutionFdpData = Record<InstitutionFdpData>({
  id: undefined,
  name: undefined,

  contact_url: undefined,
  contact_phone: undefined,
  contact_email: undefined,

  login_url: undefined,

  channelData: undefined,
  channelDataByType: undefined,
});

export type InstitutionPlaidData = {
  id: string | null;
  name: string | null;
};

export const mkInstitutionPlaidData = Record<InstitutionPlaidData>({
  id: null,
  name: null,
});

export type InstitutionFinicityData = {
  id: string | null;
  name: string | null;
};

export const mkInstitutionFinicityData = Record<InstitutionFinicityData>({
  id: null,
  name: null,
});

export type FiIssue = {
  issueType: FiIssueType;
  channel: FiChannel;
  issueSource: 'QCS' | 'AGGREGATOR';
  shortMessage: string;
  longMessage: string;
  channelIssues?: { [k in FiChannel]: FiIssue[] }; // institution endpoint only
} & (
  | {
      issueType: 'FULL_OUTAGE' | 'PARTIAL_OUTAGE' | 'INFO';
      eventStartAt?: string;
      eventEndAt?: string;
      scheduleStartDate: undefined;
      scheduleEndDate: undefined;
      scheduleStartTime: undefined;
      scheduleEndTime: undefined;
    }
  | {
      issueType: 'SCHEDULED_OUTAGE';
      scheduleStartDate: string;
      scheduleEndDate: string;
      scheduleStartTime: string;
      scheduleEndTime: string;
      eventStartAt: undefined;
      eventEndAt: undefined;
    }
);

/** Should only be exported for testing */
export const mkFiIssueInternal = Record<FiIssue>({
  issueType: 'INFO',
  channel: 'FDP_DEFAULT',
  issueSource: 'QCS',
  shortMessage: '',
  longMessage: '',
  eventStartAt: undefined,
  eventEndAt: undefined,
  scheduleStartDate: undefined,
  scheduleEndDate: undefined,
  scheduleStartTime: undefined,
  scheduleEndTime: undefined,
  channelIssues: undefined,
});

const transformFiIssueMessage = (fiIssue: FiIssue) => {
  let text = fiIssue.longMessage;
  if (fiIssue.issueType === 'SCHEDULED_OUTAGE') {
    const startDate = DateTime.fromISO(fiIssue.scheduleStartDate).toFormat('LLL dd');
    const endDate = DateTime.fromISO(fiIssue.scheduleEndDate).toFormat('LLL dd');

    const startDT = DateTime.fromISO(fiIssue.scheduleStartTime, { zone: 'UTC' }).toLocal();
    const startTime = startDT.toLocaleString(DateTime.TIME_SIMPLE);
    const endTime = DateTime.fromISO(fiIssue.scheduleEndTime, { zone: 'UTC' })
      .toLocal()
      .toLocaleString(DateTime.TIME_SIMPLE);
    const timeZone = startDT.toFormat('ZZZZ');

    text = startDate ? text.replaceAll('$scheduleStartDate', startDate) : text;
    text = endDate ? text.replaceAll('$scheduleEndDate', endDate) : text;
    text = startTime ? text.replaceAll('$scheduleStartTime', startTime) : text;
    text = endTime ? text.replaceAll('$scheduleEndTime', endTime) : text;
    text = timeZone ? text.replaceAll('$timeZone', timeZone) : text;
  }

  return text;
};

export const mkFiIssue = (fiIssue: FiIssue) =>
  mkFiIssueInternal({
    ...fiIssue,
    longMessage: transformFiIssueMessage(fiIssue),
  });

export type Institution = {
  id: string;
  name: string;
  url: string | null;

  logo?: string | null;
  logoUrl?: string | null;
  logoStyle?: string | null;

  aggregator?: string | null;
  ofx: InstitutionOfxData | null;
  fds: InstitutionFdsData | null;
  fdp: InstitutionFdpData | null;
  plaid: InstitutionPlaidData | null;
  finicity: InstitutionFinicityData | null;
  fiIssue: RecordOf<FiIssue> | null;
};

export const mkInstitution = Record<Institution>({
  id: '0',
  name: 'unknown',
  url: '',

  logo: undefined,
  logoUrl: undefined,
  logoStyle: undefined,

  aggregator: undefined,
  ofx: mkInstitutionOfxData(),
  fds: mkInstitutionFdsData(),
  fdp: mkInstitutionFdpData(),
  plaid: mkInstitutionPlaidData(),
  finicity: mkInstitutionFinicityData(),
  fiIssue: null,
});

export type InstitutionStore = QcsSyncResourceStoreProps<Institution> & {
  popularIds: ImList<string> | null;
};

export const mkInstitutionsStore = Record<InstitutionStore>({
  resourcesById: ImMap(),
  lastSyncDate: null,

  loadPending: true,
  isLoading: false,

  resourceName: 'institutions',

  popularIds: null,
});

// -- Extend store --
export const INSTITUTIONS_REDUCER_KEY = 'institutionsStore';

declare module 'app-components/utils/redux-store/rootState' {
  export default interface RootState {
    [INSTITUTIONS_REDUCER_KEY]: RecordOf<InstitutionStore>;
  }
}
